/**
 * A constraint-based mechanics engine for school statics and dynamics.
 *
 * See `docs/FeatureDesigns/mechanics-engine-target.md`. Pure logic — no React, no
 * DOM — so jest can hold it to the textbook, which is the only way an engine
 * whose whole purpose is being right stays right.
 */
import { registerBuiltInKinds } from './kinds';

registerBuiltInKinds();

export type {
    Vec2, Part, Port, PortRole, PortJoins, JoinKind, PortRef, Rope, Bond, Link, World,
    Body, CoordRef, Constraint, AppliedForce, RopeContact, Problem, PartKindKey,
} from './types';
export * as vec from './vec';

export type { PartKind, Freedom, Support } from './registry';
export { reconcile } from './reconcile';
export { settle, stands } from './settle';
export { equilibrium, diagramOf } from './equilibrium';
export type { Equilibrium, Unknown } from './equilibrium';
export { ropeDiagrams, partDiagram } from './freeBody';
export { registerKind, kindOf, allKinds, defOf, JOINS } from './registry';
export {
    registerBuiltInKinds, anchor, sheave, weight, ropeEnd, table, incline, springBalance,
    ANCHOR_EYE, weightHalf, WEIGHT_HALF_W, WEIGHT_HALF_MIN, WEIGHT_HALF_MAX, WEIGHT_ON_FACE_HALF, SHEAVE_R, SHEAVE_R_ON_FACE, SHEAVE_R_MIN, SHEAVE_R_MAX, wrapOf, corners, apexHeight, BALANCE_HALF,
} from './kinds';
export type { AnchorData, SheaveData, WeightData, RopeEndData, SurfaceData, SpringBalanceData } from './kinds';

export {
    partById, ropesOf, bondsOf, portsOf, allPorts, portAt, contactPoint,
    segmentsOf, measureRope, bodiesOf, bodyIndex, isFree, effectivePort, outward,
    extentAlong, frameOf, supportOf, freedomOf, runLength, spansOf, spanId, tensionOf, tensionNames, normalNames, measureSpans, slackOf, portLabel,
    anyPinned, released,
} from './world';
export type { Segment, Span } from './world';

export { constraintsOf } from './constraints';
export { solve, isExact, GRAM } from './solve';
export type { Solution, SolveInput } from './solve';

export { freeBodies, checkNewton, SIDEWAYS } from './freeBody';
export type { FreeBody, BodyForce } from './freeBody';

export type { Snap } from './join';
export { joinPorts, canJoin, canTake, carriesFrom, snapTargets, snapNear, joinTargets, nearestTarget, SNAP_RADIUS, joinsAt, joinKindOf, openEndOf, openRopeAt, looseEndOf, remeasure } from './join';
export { diagnose, diagnoseStructure, diagnoseSolution, joinNeed, isBuildable } from './diagnose';
export type { JoinNeed } from './diagnose';
export { derive, carried, reading, NOTHING_DERIVED } from './derive';
export type { Derived } from './derive';
export { PRESETS, openingBench, fixedPulley, movablePulley, movableTied, tackle, movableSystem, atwood, onTable, onRamp } from './presets';
export type { Preset } from './presets';
export {
    GRID, snap, mintId, place, placeRope, pullEndTo, dropIdlePins, reshape, resizePart, seatBolt, fastening, fasten, removePart, removeLink, bondsHolding, movePart, tiesTaut, setData,
    rest, surfacesFor, seatAt, mountAt, boltGap, setBoltGap, setSlack, setRopeLength, shortestRope, windable, pin, retie,
    ropeEndTargets, moveRopeEnd,
} from './edit';

export { buildRun, at, worldAt, runDuration } from './timeline';
export type { Run, Phase, RunEvent, Snapshot } from './timeline';
